import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { useMemo, useRef, useEffect, useState, useCallback } from 'react';

import type { OptionPricePoint } from '@/types/optionDiscovery';
import { brandColors } from '@utils/color-utils';
import { formatOptionPrice } from '@utils/price-formatting';

import {
  PriceLine,
  CurrentPriceCircle,
  ChartAnimations,
  TimeRangeIndicator,
} from '../market/VisxChart/PriceVisualization';
import type { DataPoint } from '../market/VisxChart/types';

// Custom margin - minimal left margin since price labels are removed
const CHART_MARGIN = { top: 10, right: 10, bottom: 30, left: 0 };

/**
 * Generate nice round numbers for grid lines
 */
function generatePriceGridLines(
  minPrice: number,
  maxPrice: number,
  targetLines = 5,
): number[] {
  const range = maxPrice - minPrice;
  if (range <= 0) {
    return [];
  }

  // Find a nice step size
  const rawStep = range / targetLines;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude;

  let niceStep: number;
  if (normalizedStep <= 1) {
    niceStep = magnitude;
  } else if (normalizedStep <= 2) {
    niceStep = 2 * magnitude;
  } else if (normalizedStep <= 5) {
    niceStep = 5 * magnitude;
  } else {
    niceStep = 10 * magnitude;
  }

  // Generate lines at nice intervals
  const lines: number[] = [];
  const start = Math.ceil(minPrice / niceStep) * niceStep;

  for (let price = start; price <= maxPrice; price += niceStep) {
    lines.push(price);
  }

  return lines;
}

interface PriceGridProps {
  yScale: (price: number) => number;
  innerWidth: number;
  innerHeight: number;
  minPrice: number;
  maxPrice: number;
}

function PriceGrid({
  yScale,
  innerWidth,
  innerHeight,
  minPrice,
  maxPrice,
}: PriceGridProps) {
  const gridLines = useMemo(
    () => generatePriceGridLines(minPrice, maxPrice, 5),
    [minPrice, maxPrice],
  );

  return (
    <g>
      {gridLines.map(price => {
        const y = yScale(price);
        // Skip if out of bounds
        if (y < 0 || y > innerHeight) {
          return null;
        }

        return (
          <g key={price}>
            {/* Grid line */}
            <line
              x1={0}
              x2={innerWidth}
              y1={y}
              y2={y}
              stroke="rgba(255, 255, 255, 0.1)"
              strokeDasharray="4,4"
            />
            {/* Price label on left */}
            <text
              x={-8}
              y={y}
              textAnchor="end"
              alignmentBaseline="middle"
              fill="rgba(255, 255, 255, 0.5)"
              fontSize={10}
            >
              ${formatOptionPrice(price)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

interface OptionPriceChartProps {
  priceHistory: OptionPricePoint[];
  priceDirection: 'up' | 'down';
  showTimeRange?: boolean;
  onHover?: (timestamp: number | null) => void;
  hoveredTimestamp?: number | null;
  onXPositionChange?: (x: number | null) => void;
  zoomLevel?: number;
  panOffset?: number;
  onZoomChange?: (zoomLevel: number) => void;
  onPanChange?: (panOffset: number) => void;
  animate?: boolean;
  showCurrentPrice?: boolean;
  lineColor?: string;
}

// Zoom constraints
const MIN_VISIBLE_MS = 5 * 60 * 1000; // Minimum 5 minutes visible
const ZOOM_SENSITIVITY = 0.1;

export function OptionPriceChart({
  priceHistory,
  priceDirection,
  showTimeRange = true,
  onHover,
  hoveredTimestamp,
  onXPositionChange,
  zoomLevel: controlledZoomLevel,
  panOffset: controlledPanOffset,
  onZoomChange,
  onPanChange,
  animate = false,
  showCurrentPrice = true,
  lineColor: customLineColor,
}: OptionPriceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Use controlled zoom/pan if provided, otherwise use local state
  const [localZoomLevel, setLocalZoomLevel] = useState(1);
  const [localPanOffset, setLocalPanOffset] = useState(0);
  
  const zoomLevel = controlledZoomLevel ?? localZoomLevel;
  const panOffset = controlledPanOffset ?? localPanOffset;
  
  const setZoomLevel = (value: number | ((prev: number) => number)) => {
    const newValue = typeof value === 'function' ? value(zoomLevel) : value;
    if (onZoomChange) {
      onZoomChange(newValue);
    } else {
      setLocalZoomLevel(newValue);
    }
  };
  
  const setPanOffset = (value: number | ((prev: number) => number)) => {
    const newValue = typeof value === 'function' ? value(panOffset) : value;
    if (onPanChange) {
      onPanChange(newValue);
    } else {
      setLocalPanOffset(newValue);
    }
  };

  // Touch handling refs
  const lastTouchDistance = useRef(0);
  const lastTouchX = useRef(0);
  const isPinching = useRef(false);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Reset zoom when data changes significantly
  useEffect(() => {
    setZoomLevel(1);
    setPanOffset(0);
  }, [priceHistory.length > 0 ? priceHistory[0].timestamp : 0]);

  const { width, height } = dimensions;
  const margin = CHART_MARGIN;
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  // Convert to DataPoint format for PriceLine
  const allData: DataPoint[] = useMemo(
    () =>
      priceHistory.map(p => ({
        timestamp: p.timestamp,
        price: p.price,
      })),
    [priceHistory],
  );

  // Calculate visible data based on zoom and pan
  const { data, visibleTimeRange } = useMemo(() => {
    if (allData.length === 0) {
      return { data: [], visibleTimeRange: { min: 0, max: 0 } };
    }

    const allTimestamps = allData.map(d => d.timestamp);
    const fullMinTime = Math.min(...allTimestamps);
    const fullMaxTime = Math.max(...allTimestamps);
    const fullRange = fullMaxTime - fullMinTime;

    // Calculate visible time range based on zoom
    const visibleRange = Math.max(fullRange * zoomLevel, MIN_VISIBLE_MS);

    // Calculate start/end based on pan (pan=0 means showing most recent)
    const maxPanOffset = Math.max(0, fullRange - visibleRange);
    const actualPanMs = panOffset * maxPanOffset;

    const visibleMax = fullMaxTime - actualPanMs;
    const visibleMin = visibleMax - visibleRange;

    // Filter data to visible range
    const visibleData = allData.filter(
      d => d.timestamp >= visibleMin && d.timestamp <= visibleMax,
    );

    return {
      data: visibleData,
      visibleTimeRange: { min: visibleMin, max: visibleMax },
    };
  }, [allData, zoomLevel, panOffset]);

  // Calculate scales for visible data
  const { xScale, yScale, timeRangeMs, minPrice, maxPrice } = useMemo(() => {
    if (data.length === 0 || innerWidth <= 0 || innerHeight <= 0) {
      return {
        xScale: null,
        yScale: null,
        timeRangeMs: 0,
        minPrice: 0,
        maxPrice: 0,
      };
    }
    
    // Debug: Log data length for flat lines
    if (data.length > 0) {
      const prices = data.map(d => d.price);
      const allSame = prices.every(p => p === prices[0]);
      if (allSame && data.length === 1) {
        console.warn('[OptionPriceChart] Only 1 data point for flat line - line will not render');
      }
    }

    const prices = data.map(d => d.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);

    // Add padding to price range
    // When all prices are the same (flat line), ensure we have enough padding to see the line
    const priceRange = max - min;
    let pricePadding: number;
    if (priceRange === 0) {
      // For flat lines, use a percentage of the price, but ensure minimum padding
      // For very small prices (like $0.10), use a larger percentage to ensure visibility
      if (min < 1) {
        pricePadding = Math.max(min * 0.5, 0.05); // 50% of price or $0.05 minimum for small prices
      } else {
        pricePadding = Math.max(min * 0.1, 0.1); // 10% of price or $0.10 minimum for larger prices
      }
    } else {
      pricePadding = Math.max(priceRange * 0.1, 0.01);
    }

    const xScaleResult = scaleTime({
      domain: [visibleTimeRange.min, visibleTimeRange.max],
      range: [0, innerWidth],
    });

    const yScaleResult = scaleLinear({
      domain: [min - pricePadding, max + pricePadding],
      range: [innerHeight, 0],
    });

    return {
      xScale: xScaleResult,
      yScale: yScaleResult,
      timeRangeMs: visibleTimeRange.max - visibleTimeRange.min,
      minPrice: min - pricePadding,
      maxPrice: max + pricePadding,
    };
  }, [data, visibleTimeRange, innerWidth, innerHeight]);

  // Zoom handler
  const handleZoom = useCallback((delta: number) => {
    setZoomLevel(prev => {
      const newZoom = prev * (1 - delta * ZOOM_SENSITIVITY);
      // Clamp between showing minimum time and all data
      return Math.max(0.1, Math.min(1, newZoom));
    });
  }, [setZoomLevel]);

  // Pan handler
  const handlePan = useCallback(
    (deltaX: number) => {
      if (zoomLevel >= 1) {
        return;
      } // Can't pan when showing all data

      setPanOffset(prev => {
        const panDelta = deltaX / innerWidth;
        return Math.max(0, Math.min(1, prev + panDelta));
      });
    },
    [zoomLevel, innerWidth, setPanOffset],
  );

  // Wheel handler - use native event listener to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      // Horizontal scroll = pan, vertical scroll = zoom
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        handlePan(e.deltaX * 0.01);
      } else {
        handleZoom(e.deltaY > 0 ? -1 : 1);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleZoom, handlePan]);

  // Touch handlers for pinch zoom and hover
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      isPinching.current = true;
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      lastTouchDistance.current = Math.abs(dx);
    } else if (e.touches.length === 1) {
      lastTouchX.current = e.touches[0].clientX;
      // Trigger hover on touch start for single touch
      if (onHover && xScale && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const svgX = e.touches[0].clientX - rect.left - margin.left;
        const timestamp = xScale.invert(svgX);
        onHover(typeof timestamp === 'number' ? timestamp : timestamp.getTime());
      }
    }
  }, [onHover, xScale, margin.left]);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && isPinching.current) {
        const dx = e.touches[1].clientX - e.touches[0].clientX;
        const distance = Math.abs(dx);

        if (lastTouchDistance.current > 0) {
          const scale = distance / lastTouchDistance.current;
          if (scale > 1.02) {
            handleZoom(1); // Zoom in
            lastTouchDistance.current = distance;
          } else if (scale < 0.98) {
            handleZoom(-1); // Zoom out
            lastTouchDistance.current = distance;
          }
        }
      } else if (e.touches.length === 1 && !isPinching.current) {
        // Handle hover for single touch
        if (onHover && xScale && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const svgX = e.touches[0].clientX - rect.left - margin.left;
          const timestamp = xScale.invert(svgX);
          onHover(typeof timestamp === 'number' ? timestamp : timestamp.getTime());
        }
        
        // Also handle pan if needed (optional - you might want to disable pan on single touch)
        // const deltaX = lastTouchX.current - e.touches[0].clientX;
        // handlePan(deltaX * 0.005);
        // lastTouchX.current = e.touches[0].clientX;
      }
    },
    [handleZoom, onHover, xScale, margin.left],
  );

  const handleTouchEnd = useCallback(() => {
    isPinching.current = false;
    lastTouchDistance.current = 0;
    // Clear hover on touch end
    if (onHover) {
      onHover(null);
    }
  }, [onHover]);

  // Get current price position
  const currentPricePosition = useMemo(() => {
    if (!xScale || !yScale || data.length === 0) {
      return null;
    }

    const lastPoint = data[data.length - 1];
    return {
      x: xScale(lastPoint.timestamp),
      y: yScale(lastPoint.price),
    };
  }, [xScale, yScale, data]);

  const lineColor = customLineColor ?? (priceDirection === 'up' ? brandColors.green : brandColors.red);

  // Handle mouse move to find timestamp at cursor position
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!onHover || !xScale || !containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const svgX = e.clientX - rect.left - margin.left;
      const timestamp = xScale.invert(svgX);
      onHover(typeof timestamp === 'number' ? timestamp : timestamp.getTime());
    },
    [onHover, xScale, margin.left],
  );

  const handleMouseLeave = useCallback(() => {
    if (onHover) {
      onHover(null);
    }
  }, [onHover]);

  // Calculate vertical line position and notify parent
  const verticalLineX =
    hoveredTimestamp && xScale
      ? xScale(hoveredTimestamp)
      : null;

  // Notify parent of X position for spanning line
  useEffect(() => {
    if (onXPositionChange) {
      const x = verticalLineX !== null ? margin.left + verticalLineX : null;
      onXPositionChange(x);
    }
  }, [verticalLineX, margin.left, onXPositionChange]);

  if (width === 0 || height === 0) {
    return (
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center"
      >
        <div className="text-white/40 text-sm">Loading chart...</div>
      </div>
    );
  }

  if (data.length < 2 || !xScale || !yScale) {
    return (
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center"
      >
        <div className="text-white/40 text-sm">Not enough data</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full touch-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <svg
        width={width}
        height={height}
        className="overflow-visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <ChartAnimations>
          <Group left={margin.left} top={margin.top}>
            {/* Price Grid Lines - removed */}

            {/* Price Line */}
            <PriceLine
              data={data}
              xScale={xScale}
              yScale={yScale}
              color={lineColor}
              animate={animate}
            />

            {/* Current Price Circle */}
            {showCurrentPrice && currentPricePosition && (
              <CurrentPriceCircle
                x={currentPricePosition.x}
                y={currentPricePosition.y}
                color={lineColor}
              />
            )}
          </Group>

          {/* Time Range Indicator */}
          {showTimeRange && (
          <TimeRangeIndicator
            timeRangeMs={timeRangeMs}
            width={width}
            height={height}
          />
          )}

          {/* Zoom indicator */}
          {zoomLevel < 1 && (
            <text
              x={width - 10}
              y={20}
              textAnchor="end"
              fill="rgba(255,255,255,0.4)"
              fontSize={11}
            >
              {Math.round(zoomLevel * 100)}%
            </text>
          )}
        </ChartAnimations>
      </svg>
    </div>
  );
}
